<script setup lang="ts">
/* The studio's one "Back with you" panel: everything that needs the signed-in
   user's attention, wherever they are. Blockers first (validation sent it
   back), then questions, then mentions, then the three policies they have been
   carrying longest. It sits flush on the right of the workspace, toggled from
   the user's avatar. One card replies at a time, and closing the panel drops
   it: each open reply holds a live policy room. */
import { POLICY_STAGE_LABELS } from '@worker/policy-builder/client'
import { formatDistanceToNowStrict, parseISO } from 'date-fns'
import type { BackWithYouItem } from '~/utils/policy-studio/back-with-you'

const { open, items, work } = useBackWithYou()

const replyingKey = ref<string | null>(null)
watch(open, (isOpen) => {
  if (!isOpen) replyingKey.value = null
})

const SECTIONS: {
  kind: BackWithYouItem['kind']
  label: string
  icon: string
}[] = [
  { kind: 'blocker', label: 'Blockers', icon: 'i-ph-prohibit' },
  { kind: 'question', label: 'Questions', icon: 'i-ph-question' },
  { kind: 'mention', label: 'Mentions', icon: 'i-ph-at' },
]
const sections = computed(() =>
  SECTIONS.map((section) => ({
    ...section,
    items: items.value.filter((item) => item.kind === section.kind),
  })).filter((section) => section.items.length),
)

</script>

<template>
  <USidebar
    v-if="items.length || work.length"
    v-model:open="open"
    class="[--sidebar-width:26rem]"
    :ui="{ header: 'h-16 min-h-16 py-0', body: 'gap-4' }"
    close
    collapsible="offcanvas"
    side="right"
    title="Back with you"
  >
    <section
      v-for="section in sections"
      :key="section.kind"
      class="flex shrink-0 flex-col gap-2.5"
      :data-testid="`back-with-you-${section.kind}`"
    >
      <h3
        class="text-muted flex items-center gap-1.5 text-xs font-semibold uppercase"
      >
        <UIcon :name="section.icon" class="size-3.5" />
        {{ section.label }}
      </h3>
      <PolicyStudioBackWithYouCard
        v-for="item in section.items"
        :key="item.key"
        :item="item"
        :replying="replyingKey === item.key"
        @update:replying="(on) => (replyingKey = on ? item.key : null)"
      />
    </section>

    <section
      v-if="work.length"
      class="flex shrink-0 flex-col gap-1"
      data-testid="back-with-you-work"
    >
      <h3
        class="text-muted flex items-center gap-1.5 text-xs font-semibold uppercase"
      >
        <UIcon name="i-ph-list-numbers" class="size-3.5" />
        Top work
      </h3>
      <NuxtLink
        v-for="row in work"
        :key="row.id"
        :to="`/policy-studio/${row.stage}/${row.id}`"
        class="hover:bg-elevated flex flex-col gap-0.5 rounded-md px-2 py-1.5"
      >
        <span class="text-default line-clamp-2 text-sm">{{ row.title }}</span>
        <span class="text-muted text-xs">
          {{ POLICY_STAGE_LABELS[row.stage] }} ·
          {{ formatDistanceToNowStrict(parseISO(row.stageChangedAt)) }} in this
          stage
        </span>
      </NuxtLink>
    </section>
  </USidebar>
</template>
